import {
  progressCheckIconSvgElement,
  progressCurrentIconSvgElement,
  progressEmptyIconSvgElement,
} from '../constants/svgIcons';
import getProgressIndexByStep from '../services/progressService';

export default class ProgressBar {
  constructor() {
    this.step = null;
    this.progressCheckIconSvgElement = progressCheckIconSvgElement;
    this.progressCurrentIconSvgElement = progressCurrentIconSvgElement;
    this.progressEmptyIconSvgElement = progressEmptyIconSvgElement;
    this.steps = ['Select date', 'Select homes', 'Join or login', 'Verify ID', 'Get entry code'];
    this.modalStep = null;
  }

  /**
   * Generates the HTML representation of the progress steps.
   *
   * This method iterates through the steps and determines the appropriate
   * HTML to render for each step based on its position relative to the
   * current progress index. Steps before the current index are rendered
   * as checked, the current step is rendered as active, and steps after
   * the current index are rendered as unchecked.
   *
   * @returns {string} The concatenated HTML string representing the progress steps.
   */
  getHtmlSteps() {
    const currentIndex = getProgressIndexByStep(this.modalStep);
    const itemsHtml = this.steps.map((step, index) => {
      if (index < currentIndex) {
        return this.renderCheckedItem(step);
      }

      if (index === currentIndex) {
        return this.renderCurrentItem(step);
      }

      return this.renderUncheckedItem(step);
    });
    return itemsHtml.join('');
  }

  /**
   * Sets the current step of the modal.
   *
   * @param {ModalStep} modalStep - The modal step.
   */
  setModalStep(modalStep) {
    this.modalStep = modalStep;
  }

  /**
   * Renders a checked progress bar item as an HTML string.
   *
   * @param {string} text - The text to display inside the progress bar item.
   * @returns {string} The HTML string representing the checked progress bar item.
   */
  renderCheckedItem(text) {
    return `
      <li class="nhs-c-progress-bar__item nhs-c-progress-bar__item--checked">
        <span class="nhs-c-progress-bar__image">${this.progressCheckIconSvgElement}</span>
        <span class="nhs-c-progress-bar__text">${text}</span>
      </li>
    `;
  }

  /**
   * Renders the current item in the progress bar as an HTML string.
   *
   * @param {string} text - The text to display for the current progress bar item.
   * @returns {string} The HTML string representing the current progress bar item.
   */
  renderCurrentItem(text) {
    return `
      <li class="nhs-c-progress-bar__item nhs-c-progress-bar__item--current">
        <span class="nhs-c-progress-bar__image">${this.progressCurrentIconSvgElement}</span>
        <span class="nhs-c-progress-bar__text">${text}</span>
      </li>
    `;
  }

  /**
   * Renders an unchecked progress bar item as an HTML string.
   *
   * @param {string} text - The text to display for the progress bar item.
   * @returns {string} The HTML string representing the unchecked progress bar item.
   */
  renderUncheckedItem(text) {
    return `
      <li class="nhs-c-progress-bar__item">
        <span class="nhs-c-progress-bar__image">${this.progressEmptyIconSvgElement}</span>
        <span class="nhs-c-progress-bar__text">${text}</span>
      </li>
    `;
  }

  /**
   * Renders the progress bar component as an ordered list (ol) element.
   * The list contains steps generated by the `getHtmlSteps` method.
   *
   * @returns {string} The HTML string representing the progress bar component.
   */
  render() {
    return `<ol class="nhs-c-progress-bar__container nhs-mq-progress-bar__container--large" data-progress-bar>
              ${this.getHtmlSteps()}
            </ol>`;
  }
}
